import { Database, Users, Clock, AlertCircle } from 'lucide-react';
import { formatTimeAgo } from '@/lib/dashboard-utils.jsx';

export const CacheStatusPanel = ({ cacheStatus, cacheMetrics }) => (
  <div className="mb-8" data-testid="cache-status-section">
    <h2 className="text-2xl font-semibold text-[#00d1ff] mb-6 border-b border-[#00d1ff]/30 pb-3">STATS CACHE STATUS</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div className="kpi-card p-4 rounded-lg" data-testid="leagues-covered">
        <div className="flex items-center gap-2 mb-2"><Database className="w-6 h-6 text-[#00d1ff]" /><span className="text-xs text-gray-400 uppercase tracking-wide">Leagues Covered</span></div>
        <p className="text-2xl font-bold text-[#00d1ff]">{cacheMetrics.leaguesCovered}<span className="text-sm text-gray-500">/28</span></p>
      </div>
      <div className="kpi-card p-4 rounded-lg" data-testid="teams-with-stats">
        <div className="flex items-center gap-2 mb-2"><Users className="w-6 h-6 text-[#00ff41]" /><span className="text-xs text-gray-400 uppercase tracking-wide">Teams with Stats</span></div>
        <p className="text-2xl font-bold text-[#00ff41]">{cacheMetrics.teamsWithStats}</p>
      </div>
      <div className="kpi-card p-4 rounded-lg" data-testid="freshest-cache">
        <div className="flex items-center gap-2 mb-2"><Clock className="w-6 h-6 text-[#00ff41]" /><span className="text-xs text-gray-400 uppercase tracking-wide">Freshest Cache</span></div>
        <p className="text-xl font-bold text-[#00ff41]">{formatTimeAgo(cacheMetrics.freshestCache)}</p>
      </div>
      <div className="kpi-card p-4 rounded-lg" data-testid="stalest-cache">
        <div className="flex items-center gap-2 mb-2"><AlertCircle className="w-6 h-6 text-[#ff0033]" /><span className="text-xs text-gray-400 uppercase tracking-wide">Stalest Cache</span></div>
        <p className="text-xl font-bold text-[#ff0033]">{formatTimeAgo(cacheMetrics.stalestCache)}</p>
        {cacheMetrics.stalestLeague && <p className="text-xs text-gray-500 mt-1">{cacheMetrics.stalestLeague}</p>}
      </div>
    </div>
    <div className="chart-container p-6 rounded-lg" data-testid="cache-overview-table">
      <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">LEAGUE CACHE OVERVIEW</h3>
      {cacheStatus.length === 0 ? (
        <div className="text-center py-8 text-gray-500"><Database className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No cache data available</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="cache-table">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">League</th>
                <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Standings</th>
                <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Detailed Stats</th>
                <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Teams Cached</th>
                <th className="text-right py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Last Refresh</th>
              </tr>
            </thead>
            <tbody>
              {cacheStatus.map((league) => (
                <tr key={league.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-testid={`cache-row-${league.id}`}>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{league.flag}</span>
                      <div><div className="font-semibold text-white">{league.name}</div><div className="text-xs text-gray-500">{league.country}</div></div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center"><StatusBadge status={league.standingsStatus} /></td>
                  <td className="py-4 px-4 text-center"><StatusBadge status={league.statsStatus} /></td>
                  <td className="py-4 px-4 text-center text-white font-semibold">{league.teamsCached}</td>
                  <td className="py-4 px-4 text-right text-gray-400">{formatTimeAgo(league.lastRefresh)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cacheStatus.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-800">
          <h4 className="text-sm font-semibold text-[#00d1ff] mb-4">CACHE FRESHNESS TIMELINE</h4>
          <div className="space-y-2">
            {cacheStatus.map((league) => {
              const daysAgo = league.daysAgo || 14;
              const barWidth = Math.min((daysAgo / 14) * 100, 100);
              const barColor = daysAgo < 2 ? '#00ff41' : daysAgo < 7 ? '#eab308' : '#ff0033';
              return (
                <div key={league.id} className="flex items-center gap-3" data-testid={`timeline-${league.id}`}>
                  <span className="text-xs w-32 text-gray-400 text-right">{league.flag} {league.name}</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-2 relative overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${100 - barWidth}%`, backgroundColor: barColor }} />
                  </div>
                  <span className="text-xs w-16 text-gray-500">{league.daysAgo ? `${Math.floor(league.daysAgo)}d` : 'N/A'}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-4 mt-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#00ff41]" /> &lt; 2 days</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> 2-7 days</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#ff0033]" /> &gt; 7 days</span>
          </div>
        </div>
      )}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const dotColor = status.color === 'green' ? 'bg-[#00ff41]' : status.color === 'yellow' ? 'bg-yellow-500' : 'bg-[#ff0033]';
  const textColor = status.color === 'green' ? 'text-[#00ff41]' : status.color === 'yellow' ? 'text-yellow-500' : 'text-[#ff0033]';
  return (
    <div className="flex items-center justify-center gap-2">
      <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
      <span className={`text-sm ${textColor}`}>{status.label}</span>
    </div>
  );
};
